import { z } from 'zod';
import { botLevelSchema, endReasonSchema, fleetSchema, rulesetSchema, seatSchema, shotResultSchema, } from './primitives.js';
/** DTO warstwy REST: uwierzytelnianie, lobby i historia rozgrywek. */
export const nicknameSchema = z
    .string()
    .trim()
    .min(3)
    .max(20)
    .regex(/^[\p{L}\p{N}_-]+$/u, 'Dozwolone są litery, cyfry, myślnik i podkreślnik');
export const loginRequestSchema = z.object({
    login: z.string().trim().min(3).max(120),
    password: z.string().min(8).max(200),
});
/**
 * Samodzielna rejestracja przez link zaproszenia (`docs/07-roadmapa.md`, Faza 3).
 * `inviteCode` to na razie jedna stała wartość, nie coś, co identyfikuje zapraszającego —
 * do tego służy `ref` (`userId` z linku, opcjonalny, niekryptograficzny — patrz
 * `apps/server/src/auth/accounts.ts`).
 */
/**
 * Podpowiedź "na bieżąco" w formularzu rejestracji — czy dostępność, nie ostateczne
 * rozstrzygnięcie. Unikalność (case-insensitive) i tak wymusza baza przy właściwym
 * zapisie konta (`apps/server/src/auth/accounts.ts`); między sprawdzeniem a wysłaniem
 * formularza ktoś inny mógłby zdążyć zająć ten sam nick.
 */
export const nicknameAvailableResponseSchema = z.object({
    available: z.boolean(),
});
export const registerRequestSchema = z.object({
    nickname: nicknameSchema,
    password: z.string().min(8).max(200),
    email: z.email().max(254).optional(),
    inviteCode: z.string().min(1).max(200),
    // Celowo zwykły string, nie `z.uuid()`: to pole jest tylko informacyjne (patrz
    // `apps/server/src/auth/accounts.ts`), więc zniekształcony `ref` w linku nie może
    // wywrócić całej rejestracji błędem walidacji.
    ref: z.string().max(100).optional(),
});
/** Samoobsługowa zmiana własnego hasła — `POST /api/me/password`. */
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(8).max(200),
});
export const meSchema = z.object({
    id: z.uuid(),
    nickname: nicknameSchema,
    kind: z.enum(['user', 'guest', 'bot']),
    isAdmin: z.boolean(),
});
export const createRoomRequestSchema = z.object({
    ruleset: rulesetSchema.partial().optional(),
});
export const createRoomResponseSchema = z.object({
    gameId: z.uuid(),
    code: z.string().length(6),
});
export const createBotRoomRequestSchema = z.object({
    level: botLevelSchema,
});
export const createBotRoomResponseSchema = z.object({
    gameId: z.uuid(),
});
export const gameSummarySchema = z.object({
    id: z.uuid(),
    finishedAt: z.iso.datetime().nullable(),
    opponent: z.object({ nickname: nicknameSchema }).nullable(),
    yourSeat: seatSchema,
    result: z.enum(['win', 'loss', 'abandoned']).nullable(),
    endReason: endReasonSchema.nullable(),
    durationSec: z.number().int().nullable(),
    moves: z.number().int(),
    accuracy: z.number().min(0).max(1).nullable(),
});
export const gameHistoryResponseSchema = z.object({
    items: z.array(gameSummarySchema),
    nextCursor: z.string().nullable(),
});
/**
 * Trwająca partia do powrotu (`GET /api/me/active-games`, `docs/08-decyzje.md` #24).
 * `yourTurn` jest `null`, gdy serwer nie ma pokoju w pamięci (np. zaraz po restarcie) albo
 * w fazie rozstawiania — wtedy tura nie istnieje.
 */
export const activeGameSchema = z.object({
    id: z.uuid(),
    status: z.enum(['placement', 'in_progress']),
    opponent: z.object({ nickname: nicknameSchema }).nullable(),
    yourTurn: z.boolean().nullable(),
});
export const activeGamesResponseSchema = z.object({
    items: z.array(activeGameSchema),
});
export const moveSchema = z.object({
    seq: z.number().int().min(1),
    seat: seatSchema,
    x: z.number().int().min(0).max(25),
    y: z.number().int().min(0).max(25),
    result: shotResultSchema,
    shipId: z.number().int().min(0).max(63).optional(),
});
/**
 * Szczegół zakończonej partii — dane do odtwarzacza replayu.
 *
 * Endpoint jest celowo publiczny-po-znajomości-URL (bez uwierzytelnienia): link do replayu
 * ma nadawać się do udostępnienia, a partia jest już skończona, więc redakcja floty
 * przeciwnika nie obowiązuje.
 */
export const gameDetailResponseSchema = z.object({
    id: z.uuid(),
    ruleset: rulesetSchema,
    seats: z.tuple([
        z.object({ nickname: nicknameSchema }).nullable(),
        z.object({ nickname: nicknameSchema }).nullable(),
    ]),
    fleets: z.tuple([fleetSchema, fleetSchema]),
    moves: z.array(moveSchema),
    winner: seatSchema.nullable(),
    endReason: endReasonSchema,
    startedAt: z.iso.datetime().nullable(),
    finishedAt: z.iso.datetime(),
});
/** Statystyki profilu — liczone na żądanie z `games`/`game_players`/`moves`, patrz `docs/04` §4.4. */
export const profileStatsResponseSchema = z.object({
    gamesPlayed: z.number().int().min(0),
    wins: z.number().int().min(0),
    winRate: z.number().min(0).max(1).nullable(),
    /** Łączna celność: suma trafień / suma strzałów, nie średnia z partii. */
    accuracy: z.number().min(0).max(1).nullable(),
    longestHitStreak: z.number().int().min(0),
    /** `null`, gdy gracz nie ma jeszcze żadnej rozstrzygniętej partii (win/loss) w historii. */
    currentStreak: z
        .object({
        type: z.enum(['win', 'loss']),
        count: z.number().int().min(1),
    })
        .nullable(),
    /** Zwycięska partia z najmniejszą liczbą własnych strzałów. `null`, gdy brak zwycięstw. */
    bestGame: z
        .object({
        gameId: z.uuid(),
        shots: z.number().int().min(1),
        opponent: z.object({ nickname: nicknameSchema }).nullable(),
        finishedAt: z.iso.datetime(),
    })
        .nullable(),
    /** Ranking Glicko-2 (`docs/08-decyzje.md` #20) — 1500/350 domyślnie, bez rozegranej partii rankingowej. */
    rating: z.number(),
    ratingDeviation: z.number(),
});
/**
 * Publiczna kolejka matchmakingu (Faza 3, `docs/08-decyzje.md` #20). `status: 'idle'` —
 * gracz nigdy nie dołączył albo zrezygnował; `'waiting'` — czeka na przeciwnika;
 * `'matched'` — sparowany, `gameId` gotowe do wejścia.
 */
export const matchmakingStatusResponseSchema = z.object({
    status: z.enum(['idle', 'waiting', 'matched']),
    gameId: z.uuid().optional(),
});
/** Jeden wiersz tablicy wyników — patrz `GET /api/leaderboard`. */
export const leaderboardEntrySchema = z.object({
    nickname: nicknameSchema,
    rating: z.number(),
    ratingDeviation: z.number(),
});
export const leaderboardResponseSchema = z.object({
    items: z.array(leaderboardEntrySchema),
});
/**
 * Ostatni przeciwnicy i ulubieni (Faza 4, `docs/08-decyzje.md` #21) — lista do "wyzwania"
 * na pojedynek, patrz `challengeResponseSchema` niżej.
 */
export const opponentSchema = z.object({
    userId: z.uuid(),
    nickname: nicknameSchema,
    isFavorite: z.boolean(),
    lastPlayedAt: z.iso.datetime().nullable(),
    /** Bilans bezpośrednich starć z tym przeciwnikiem, tylko zakończone partie. */
    headToHead: z.object({
        wins: z.number().int().min(0),
        losses: z.number().int().min(0),
    }),
});
export const opponentsResponseSchema = z.object({
    items: z.array(opponentSchema),
});
export const favoriteSchema = z.object({
    userId: z.uuid(),
    nickname: nicknameSchema,
    favoritedAt: z.iso.datetime(),
});
export const favoritesResponseSchema = z.object({
    items: z.array(favoriteSchema),
});
/**
 * Lista graczy do wyzwania bez wcześniejszej wspólnej partii (`docs/08-decyzje.md` #23).
 * `in_game` — siedzi w partii, która już wystartowała (wyzwanie zwróciłoby `TARGET_BUSY`);
 * `online` — aktywny w aplikacji w ostatnich kilkudziesięciu sekundach.
 */
export const playerStatusSchema = z.enum(['online', 'in_game', 'offline']);
export const playerSchema = z.object({
    userId: z.uuid(),
    nickname: nicknameSchema,
    isFavorite: z.boolean(),
    status: playerStatusSchema,
});
export const playersResponseSchema = z.object({
    items: z.array(playerSchema),
});
export const createChallengeRequestSchema = z.object({
    targetUserId: z.uuid(),
});
export const createChallengeResponseSchema = z.object({
    id: z.uuid(),
});
/** `rematch` — oferta odnosi się do konkretnej zakończonej partii, patrz `sourceGameId`. */
export const challengeKindSchema = z.enum(['challenge', 'rematch']);
export const incomingChallengeSchema = z.object({
    id: z.uuid(),
    kind: challengeKindSchema,
    sourceGameId: z.uuid().nullable(),
    from: z.object({ userId: z.uuid(), nickname: nicknameSchema }),
    createdAt: z.iso.datetime(),
});
export const outgoingChallengeSchema = z.object({
    id: z.uuid(),
    kind: challengeKindSchema,
    sourceGameId: z.uuid().nullable(),
    to: z.object({ userId: z.uuid(), nickname: nicknameSchema }),
    status: z.enum(['pending', 'accepted']),
    gameId: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
});
export const challengesResponseSchema = z.object({
    incoming: z.array(incomingChallengeSchema),
    outgoing: z.array(outgoingChallengeSchema),
});
export const acceptChallengeResponseSchema = z.object({
    gameId: z.uuid(),
});
/**
 * Odpowiedź na zgłoszenie rewanżu (`POST /api/games/:gameId/rematch`) — `offered`, dopóki
 * druga strona się nie zgodzi, `ready` od razu, gdy zgoda już była (lustrzana oferta) albo
 * przeciwnikiem jest bot (patrz `createRematchOffer`, `08-decyzje.md` #10).
 */
export const rematchResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('offered'), id: z.uuid() }),
    z.object({ status: z.literal('ready'), gameId: z.uuid() }),
]);
/**
 * Panel admina. Metryki produktowe (`activeUsersLast7Days`, `placementAbandonmentRate`)
 * liczone z `app_events`, patrz `docs/06` §6.3 — `null`, dopóki nie ma jeszcze danych.
 */
export const adminStatsResponseSchema = z.object({
    totalAccounts: z.number().int().min(0),
    totalGames: z.number().int().min(0),
    gamesInProgress: z.number().int().min(0),
    gamesFinishedLast7Days: z.number().int().min(0),
    totalMoves: z.number().int().min(0),
    /** Odrębni użytkownicy z co najmniej jednym zdarzeniem w ostatnich 7 dniach (proxy WAU). */
    activeUsersLast7Days: z.number().int().min(0),
    /** `placement_abandoned` / (`placement_abandoned` + `placement_done`), całościowo. */
    placementAbandonmentRate: z.number().min(0).max(1).nullable(),
});
/** Jedno konto z perspektywy panelu obsługi użytkowników. */
export const adminUserSchema = z.object({
    id: z.uuid(),
    nickname: z.string(),
    email: z.string().nullable(),
    kind: z.enum(['user', 'guest', 'bot']),
    isAdmin: z.boolean(),
    status: z.enum(['active', 'blocked']),
    createdAt: z.iso.datetime(),
    lastSeenAt: z.iso.datetime().nullable(),
    /** Nick zapraszającego, jeśli konto powstało z linku zaproszenia i to konto nadal istnieje. */
    invitedByNickname: z.string().nullable(),
});
export const adminUsersResponseSchema = z.object({
    items: z.array(adminUserSchema),
});
export const setUserStatusRequestSchema = z.object({
    status: z.enum(['active', 'blocked']),
});
export const setUserAdminRequestSchema = z.object({
    isAdmin: z.boolean(),
});
/**
 * Wymuszony reset hasła (panel admina) — hasło wraca RAZ, w tej jednej odpowiedzi;
 * nigdzie się nie zapisuje, patrz `apps/server/src/admin/users.ts`'s `resetUserPassword`.
 */
export const resetPasswordResponseSchema = z.object({
    password: z.string(),
});
/**
 * Zdarzenia produktowe zgłaszane przez klienta — patrz `docs/06-obserwowalnosc-i-rodo.md` §6.3.
 * Zdarzenia, które serwer i tak widzi z komend (`room_created`, `game_started`...), NIE są
 * tu wymienione: klient nie ma jak ich sfałszować, bo serwer loguje je sam.
 */
export const clientEventTypeSchema = z.enum([
    'session_start',
    'placement_started',
    'placement_done',
    'placement_abandoned',
    'error_shown',
]);
const eventPropValueSchema = z.union([z.string().max(200), z.number(), z.boolean(), z.null()]);
export const trackEventRequestSchema = z.object({
    type: clientEventTypeSchema,
    gameId: z.uuid().optional(),
    props: z.record(z.string(), eventPropValueSchema).optional(),
});
/** Kod do wklejenia w link zaproszenia — patrz `env.SIGNUP_INVITE_CODE`. */
export const inviteCodeResponseSchema = z.object({
    code: z.string(),
});
//# sourceMappingURL=http.js.map